import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAdmin } from './AdminContentEditor';
import { useAuth } from '../context/AuthContext';
import { 
  Mail, 
  MousePointer, 
  LayoutDashboard, 
  Rocket, 
  FileText, 
  Settings,
  X,
  Cloud,
  Sparkles,
  Filter,
  Layers,
  Server,
  Lightbulb,
  FlaskConical,
  Lock,
  Armchair,
  Clock,
  CheckCircle,
  Eye,
  EyeOff,
  Pencil,
  Save,
  Upload,
  Trash2,
  Plus,
  Image as ImageIcon,
  ChevronDown,
  ChevronUp,
  Loader2
} from 'lucide-react';

const API_URL = window.location.origin;

// Default published resources (only Women Business Soiree lab is public)
const defaultPublishedResources = [
  {
    id: 'women-biz-soiree',
    type: 'labs',
    platform: 'Lab',
    title: 'AI for Business Growth',
    description: 'Workshop content from Women Business Soirée. AI tools for operations, marketing & customer service.',
    icon: FlaskConical,
    color: '#E86A4A',
    author: 'Women Business Soirée',
    path: '/womenbusinesssoiree',
    tags: ['ai', 'business', 'workshop'],
    tools: ['notion', 'canva', 'airtable'],
    isLab: true,
    requiresAuth: true,
    labType: 'soiree',
  },
];

// Default draft resources (hidden from public, visible in admin)
const defaultDraftResources = [
  {
    id: 'front-row-tech',
    type: 'labs',
    platform: 'Lab',
    title: 'Front Row: Tech Company',
    description: 'Get a front row seat to building a tech startup. Monthly behind-the-scenes access.',
    icon: FlaskConical,
    color: '#8B5CF6',
    author: '@techbaenae',
    path: '/lab/frontrow/phygital',
    tags: ['startup', 'phygital', 'learning'],
    tools: ['phygital'],
    isLab: true,
    requiresAuth: true,
    labType: 'frontrow',
    status: 'draft',
  },
  {
    id: 'front-row-community',
    type: 'labs',
    platform: 'Lab',
    title: 'Front Row: Digital Community Hub',
    description: 'Get a front row seat to building a digital community platform from whiteboard to working system.',
    icon: FlaskConical,
    color: '#FF007F',
    author: '@techbaenae',
    path: '/lab/frontrow/datadreaming',
    tags: ['community', 'platform', 'learning'],
    tools: ['emergent'],
    isLab: true,
    requiresAuth: true,
    labType: 'frontrow',
    status: 'published',
    private: true, // Published but unlisted - accessible via direct URL only
  },
  {
    id: 'getting-started',
    type: 'prompts',
    platform: 'Website',
    title: 'Getting Started',
    description: 'Essential prompts to build your personal brand website.',
    icon: Rocket,
    color: '#FFD700',
    author: '@techbaenae',
    path: '/resources/prompts/getting-started',
    tags: ['website', 'beginner', 'foundation'],
    tools: ['emergent'],
    status: 'draft',
  },
  {
    id: 'custom-cursor-effects',
    type: 'prompts',
    platform: 'Website',
    title: 'Custom Cursor Effects',
    description: 'Add magical cursor trails and interactive effects to your website.',
    icon: MousePointer,
    color: '#00CED1',
    author: '@techbaenae',
    path: '/resources/prompts/custom-cursor-effects',
    tags: ['website', 'design', 'animations'],
    tools: ['emergent'],
    status: 'draft',
  },
  {
    id: 'admin-dashboard',
    type: 'prompts',
    platform: 'Website',
    title: 'Admin Dashboard & Analytics',
    description: 'Track visitors and engagement with a custom admin panel.',
    icon: LayoutDashboard,
    color: '#6A0DAD',
    author: '@techbaenae',
    path: '/resources/prompts/admin-dashboard',
    tags: ['website', 'analytics', 'admin'],
    tools: ['emergent'],
    status: 'draft',
  },
  {
    id: 'content-management',
    type: 'prompts',
    platform: 'Website',
    title: 'Content Management',
    description: 'Make your website content editable without code.',
    icon: FileText,
    color: '#FF007F',
    author: '@techbaenae',
    path: '/resources/prompts/content-management',
    tags: ['website', 'cms', 'blog'],
    tools: ['emergent'],
    status: 'draft',
  },
  {
    id: 'add-on-features',
    type: 'prompts',
    platform: 'Website',
    title: 'Add-on Features',
    description: 'Enhance with newsletter signup, booking calendar, and more.',
    icon: Settings,
    color: '#32CD32',
    author: '@techbaenae',
    path: '/resources/prompts/add-on-features',
    tags: ['website', 'integrations', 'advanced'],
    tools: ['emergent'],
    status: 'draft',
  },
  {
    id: 'saas-analytics-template',
    type: 'templates',
    platform: 'Notion',
    title: 'SaaS Analytics Dashboard',
    description: 'Track MRR, churn, and key metrics for your SaaS business.',
    icon: Layers,
    color: '#FF6B35',
    author: '@techbaenae',
    path: '/resources/templates/saas-analytics',
    tags: ['saas', 'analytics', 'metrics'],
    tools: ['notion'],
    status: 'draft',
  },
  {
    id: 'content-calendar',
    type: 'templates',
    platform: 'Notion',
    title: 'Content Calendar System',
    description: 'Plan and schedule content across all your channels.',
    icon: Filter,
    color: '#9B59B6',
    author: '@techbaenae',
    path: '/resources/templates/content-calendar',
    tags: ['content', 'planning', 'social'],
    tools: ['notion', 'beehiiv'],
    status: 'draft',
  },
  {
    id: 'ai-integration-guide',
    type: 'guides',
    platform: 'Guide',
    title: 'AI Integration Playbook',
    description: 'Step-by-step guide to adding AI features to your workflow.',
    icon: Lightbulb,
    color: '#E74C3C',
    author: '@techbaenae',
    path: '/resources/guides/ai-integration',
    tags: ['ai', 'automation', 'workflow'],
    tools: ['emergent', 'notion'],
    status: 'draft',
  },
  {
    id: 'creator-tech-stack',
    type: 'stacks',
    platform: 'Stack',
    title: 'Creator Tech Stack',
    description: 'The complete toolkit for modern content creators.',
    icon: Server,
    color: '#1ABC9C',
    author: '@techbaenae',
    path: '/resources/stacks/creator-stack',
    tags: ['tools', 'creator', 'productivity'],
    tools: ['notion', 'beehiiv', 'cloudflare'],
    status: 'draft',
  },
];

// Tool logos component
const ToolLogos = ({ tools }) => {
  // Official tool logos from workshop pages - no borders
  const toolConfig = {
    emergent: { 
      logo: 'https://customer-assets.emergentagent.com/job_caf6c523-32ed-44de-aaab-8552e94ae126/artifacts/d7ltuzdn_datadreaminglogowhite.png',
      bg: '#000'
    },
    notion: { 
      logo: 'https://upload.wikimedia.org/wikipedia/commons/4/45/Notion_app_logo.png',
      bg: '#fff'
    },
    canva: { 
      logo: 'https://customer-assets.emergentagent.com/job_caf6c523-32ed-44de-aaab-8552e94ae126/artifacts/15zeamoo_CP7Z_quEhYkDEAE%3D.webp',
      bg: '#fff'
    },
    beehiiv: { 
      logo: 'https://beehiiv-images-production.s3.amazonaws.com/static_assets/beehiiv-head-black.png',
      bg: '#FCD34D'
    },
    cloudflare: { 
      logo: 'https://upload.wikimedia.org/wikipedia/commons/9/94/Cloudflare_Logo.png',
      bg: '#fff'
    },
    phygital: { 
      logo: 'https://customer-assets.emergentagent.com/job_caf6c523-32ed-44de-aaab-8552e94ae126/artifacts/d7ltuzdn_datadreaminglogowhite.png',
      bg: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)'
    },
    airtable: { 
      logo: 'https://customer-assets.emergentagent.com/job_caf6c523-32ed-44de-aaab-8552e94ae126/artifacts/6if1ce6z_09d47a15534609898b3c26b98664751f.jpg',
      bg: '#fff'
    },
  };

  return (
    <div className="flex items-center gap-1">
      {tools.slice(0, 4).map((tool, idx) => {
        const config = toolConfig[tool];
        if (!config) {
          // Fallback for unknown tools - show first letter
          return (
            <div
              key={idx}
              className="w-5 h-5 rounded flex items-center justify-center bg-gray-500 text-white text-[8px] font-bold"
              title={tool}
            >
              {tool[0].toUpperCase()}
            </div>
          );
        }
        return (
          <div
            key={idx}
            className="w-5 h-5 rounded flex items-center justify-center overflow-hidden"
            style={{ background: config.bg }}
            title={tool}
          >
            <img 
              src={config.logo} 
              alt={tool} 
              className="w-4 h-4 object-contain"
            />
          </div>
        );
      })}
    </div>
  );
};

// Lab Access Modal - Simple email check against approved list
const LabAccessModal = ({ isOpen, onClose, onAccessGranted, lab }) => {
  const [status, setStatus] = useState('idle');
  const [email, setEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();

  const SOIREE_LOGO = 'https://customer-assets.emergentagent.com/job_caf6c523-32ed-44de-aaab-8552e94ae126/artifacts/95iizd24_transparent-fortheminds%20%281%29.png';
  const labColor = lab?.color || '#8B5CF6';

  // Reset state when modal opens with new lab
  useEffect(() => {
    if (isOpen) {
      setStatus('idle');
      setEmail('');
      setErrorMessage('');
    }
  }, [isOpen, lab?.id]);

  // Get approved emails from localStorage (admin-managed)
  const getApprovedEmails = () => {
    const labKey = `approved_emails_${lab?.labType || lab?.id || 'default'}`;
    const stored = localStorage.getItem(labKey);
    if (stored) {
      return JSON.parse(stored);
    }
    // Default approved emails for all labs
    return ['me@iamnaomithomas.com', 'naomi@techbaenae.com'];
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setErrorMessage('Please enter your email');
      return;
    }
    
    setStatus('checking');
    setErrorMessage('');
    
    // Simulate a brief check
    setTimeout(() => {
      const approvedEmails = getApprovedEmails();
      const isApproved = approvedEmails.some(
        approved => approved.toLowerCase() === email.toLowerCase().trim()
      );
      
      if (isApproved) {
        setStatus('approved');
        // Store access in localStorage using labType or id
        const labKey = lab?.labType || lab?.id || 'lab';
        localStorage.setItem(`${labKey}_approved`, 'true');
        localStorage.setItem(`${labKey}_email`, email.toLowerCase().trim());
        
        setTimeout(() => {
          onAccessGranted();
          navigate(lab?.path || '/');
        }, 1500);
      } else {
        setStatus('denied');
      }
    }, 800);
  };

  const handleTryAgain = () => {
    setStatus('idle');
    setEmail('');
    setErrorMessage('');
  };

  if (!isOpen || !lab) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="lab-access-modal"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="w-full max-w-md rounded-3xl p-8 bg-white relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
        >
          <X className="w-5 h-5 text-gray-400" />
        </button>

        {status === 'idle' ? (
          <>
            <div className="text-center mb-6">
              <div 
                className="w-20 h-20 rounded-2xl mx-auto mb-4 flex items-center justify-center"
                style={{ background: labColor }}
              >
                {lab?.labType === 'soiree' ? (
                  <img src={SOIREE_LOGO} alt={lab.title} className="h-10 object-contain" />
                ) : (
                  <FlaskConical className="w-10 h-10 text-white" strokeWidth={1.5} />
                )}
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Access {lab.title}</h3>
              <p className="text-gray-500 text-sm">
                Enter your email to access this content.
              </p>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-900 bg-white"
                  data-testid="email-input"
                  autoFocus
                />
                {errorMessage && (
                  <p className="text-red-500 text-sm mt-2">{errorMessage}</p>
                )}
              </div>

              <button
                type="submit"
                className="w-full py-3 px-4 rounded-xl font-medium text-white transition-all hover:opacity-90"
                style={{ background: labColor }}
                data-testid="submit-email-btn"
              >
                Continue
              </button>
            </form>

            <p className="text-xs text-gray-400 text-center mt-4">
              Access is limited to approved members.
            </p>
          </>
        ) : status === 'checking' ? (
          <div className="text-center py-4">
            <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" style={{ color: labColor }} />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Checking Access...</h3>
            <p className="text-gray-500 text-sm">Verifying your email</p>
          </div>
        ) : status === 'approved' ? (
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full bg-green-100 mx-auto mb-4 flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Access Granted!</h3>
            <p className="text-gray-500 text-sm mb-4">Welcome! Redirecting...</p>
            <div 
              className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mx-auto"
              style={{ borderColor: labColor, borderTopColor: 'transparent' }}
            />
          </div>
        ) : (
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full bg-red-100 mx-auto mb-4 flex items-center justify-center">
              <Lock className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Not Approved</h3>
            <p className="text-gray-600 text-sm mb-1">
              This email is not on the approved list.
            </p>
            <p className="text-gray-500 text-xs mb-6">
              {email}
            </p>
            <button
              onClick={() => {
                onClose();
                const notifySection = document.querySelector('#notify');
                if (notifySection) notifySection.scrollIntoView({ behavior: 'smooth' });
              }}
              className="w-full py-3 rounded-xl font-medium text-white transition-all"
              style={{ background: labColor }}
            >
              Request Access
            </button>
            <button
              onClick={handleTryAgain}
              className="w-full py-3 mt-2 rounded-xl font-medium text-gray-600 hover:bg-gray-100 transition-all"
            >
              Try Different Email
            </button>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

// Admin Edit Modal for resource customization
const AdminEditModal = ({ isOpen, onClose, resource, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    color: '',
    author: '',
    image_url: '',
  });
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (resource) {
      setFormData({
        title: resource.title || '',
        description: resource.description || '',
        color: resource.color || '#E86A4A',
        author: resource.author || '@techbaenae',
        image_url: resource.image_url || '',
      });
    }
  }, [resource]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await fetch(`${API_URL}/api/admin/upload`, {
        method: 'POST',
        body: formDataUpload,
      });
      
      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({ ...prev, image_url: `${API_URL}${data.url}` }));
      }
    } catch (error) {
      console.error('Upload failed:', error);
    }
    setUploading(false);
  };

  const handleSave = () => {
    onSave({ ...resource, ...formData });
    onClose();
  };

  if (!isOpen || !resource) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="w-full max-w-lg rounded-2xl p-6 bg-white relative max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Edit Resource</h3>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-900 bg-white"
              data-testid="edit-resource-title"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-900 bg-white"
              data-testid="edit-resource-description"
            />
          </div>

          {/* Color */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Theme Color</label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={formData.color}
                onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                className="w-12 h-10 rounded cursor-pointer border-0"
              />
              <input
                type="text"
                value={formData.color}
                onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                className="flex-1 px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono text-sm text-gray-900 bg-white"
              />
            </div>
          </div>

          {/* Author */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Author</label>
            <input
              type="text"
              value={formData.author}
              onChange={(e) => setFormData(prev => ({ ...prev, author: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-900 bg-white"
            />
          </div>

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Custom Image</label>
            <div className="space-y-2">
              {formData.image_url && (
                <div className="relative w-full h-32 rounded-lg overflow-hidden bg-gray-100">
                  <img src={formData.image_url} alt="Preview" className="w-full h-full object-cover" />
                  <button
                    onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))}
                    className="absolute top-2 right-2 p-1 rounded-full bg-red-500 text-white hover:bg-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="w-full py-2 px-4 rounded-lg border-2 border-dashed border-gray-300 hover:border-purple-400 transition-colors flex items-center justify-center gap-2 text-gray-500 hover:text-purple-600"
              >
                {uploading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Upload Image
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 py-2 px-4 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="flex-1 py-2 px-4 rounded-lg bg-purple-600 text-white hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
            data-testid="save-resource-btn"
          >
            <Save className="w-4 h-4" />
            Save Changes
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Resource Card Component
const ResourceCard = ({ resource, onClick, isAdmin, onEdit, onPublish, onRemove }) => {
  const Icon = resource.icon;
  
  return (
    <motion.div
      className="w-full text-left group relative overflow-hidden rounded-2xl md:rounded-3xl transition-all duration-300 hover:scale-[1.02]"
      style={{
        background: `linear-gradient(135deg, ${resource.color}15 0%, ${resource.color}30 100%)`,
        border: `1px solid ${resource.color}40`,
      }}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      data-testid={`resource-${resource.id}`}
    >
      <div 
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{
          background: `radial-gradient(circle at center, ${resource.color}20 0%, transparent 70%)`,
        }}
      />
      
      {/* Admin Edit, Publish & Remove Buttons */}
      {isAdmin && (
        <div className="absolute top-3 right-3 z-10 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-all">
          {resource.status === 'draft' && onPublish && (
            <button
              onClick={(e) => { e.stopPropagation(); onPublish(resource); }}
              className="p-2 rounded-lg bg-green-500 hover:bg-green-600 shadow-sm transition-all flex items-center gap-1"
              data-testid={`publish-resource-${resource.id}`}
              title="Publish resource"
            >
              <Eye className="w-4 h-4 text-white" />
              <span className="text-xs text-white font-medium">Publish</span>
            </button>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(resource); }}
            className="p-2 rounded-lg bg-white/90 hover:bg-white shadow-sm transition-all"
            data-testid={`edit-resource-${resource.id}`}
            title="Edit resource"
          >
            <Pencil className="w-4 h-4 text-gray-600" />
          </button>
          {onRemove && (
            <button
              onClick={(e) => { e.stopPropagation(); onRemove(resource); }}
              className="p-2 rounded-lg bg-red-500 hover:bg-red-600 shadow-sm transition-all"
              data-testid={`remove-resource-${resource.id}`}
              title="Remove resource"
            >
              <X className="w-4 h-4 text-white" />
            </button>
          )}
        </div>
      )}
      
      <button onClick={onClick} className="relative p-5 md:p-6 w-full text-left">
        <div className="flex items-start gap-4">
          {resource.image_url ? (
            <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl md:rounded-2xl flex-shrink-0 overflow-hidden">
              <img src={resource.image_url} alt={resource.title} className="w-full h-full object-cover" />
            </div>
          ) : (
            <div 
              className="w-12 h-12 md:w-14 md:h-14 rounded-xl md:rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{ background: `${resource.color}30` }}
            >
              <Icon 
                className="w-6 h-6 md:w-7 md:h-7" 
                style={{ color: resource.color }}
                strokeWidth={1.5}
              />
            </div>
          )}
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span 
                className="text-[10px] md:text-xs font-semibold px-2 py-0.5 rounded-full"
                style={{ 
                  background: `${resource.color}30`,
                  color: resource.color 
                }}
              >
                {resource.platform}
              </span>
              {resource.isLab && (
                <span className="text-[10px] md:text-xs font-semibold px-2 py-0.5 rounded-full bg-white/20 text-white flex items-center gap-1">
                  <Lock className="w-3 h-3" />
                  Invite Only
                </span>
              )}
              {resource.status === 'draft' && (
                <span className="text-[10px] md:text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 flex items-center gap-1">
                  <EyeOff className="w-3 h-3" />
                  Draft
                </span>
              )}
            </div>
            <h3 className="text-base md:text-lg font-semibold text-white mb-1 truncate">
              {resource.title}
            </h3>
            <p className="text-xs md:text-sm text-white/60 line-clamp-2 mb-2">
              {resource.description}
            </p>
            
            <div className="flex items-center justify-between">
              <span className="text-[10px] md:text-xs text-white/40">
                {resource.author}
              </span>
              <ToolLogos tools={resource.tools} />
            </div>
          </div>
        </div>
      </button>
    </motion.div>
  );
};

export const ResourcesSection = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { isAdmin } = useAdmin();
  const [showLabAccessModal, setShowLabAccessModal] = useState(false);
  const [selectedLab, setSelectedLab] = useState(null);
  const [showDrafts, setShowDrafts] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [publishedResources, setPublishedResources] = useState(defaultPublishedResources);
  const [draftResources, setDraftResources] = useState(defaultDraftResources);

  // Load resources - try backend first, fallback to localStorage
  useEffect(() => {
    const loadResources = async () => {
      // First, check localStorage for published resource IDs
      const localPublishedIds = JSON.parse(localStorage.getItem('published_resource_ids') || '[]');
      
      // Try to load from backend
      try {
        const response = await fetch(`${API_URL}/api/admin/resources`);
        if (response.ok) {
          const data = await response.json();
          const backendResources = data.resources || [];
          
          // Get IDs of resources saved in backend
          const backendResourceIds = new Set(backendResources.map(r => r.resource_id));
          
          // Separate backend resources into published and drafts
          const backendPublished = backendResources.filter(r => r.is_published);
          const backendDrafts = backendResources.filter(r => !r.is_published);
          
          // Map backend resources to frontend format
          const mapResource = (r) => {
            const defaultResource = [...defaultPublishedResources, ...defaultDraftResources]
              .find(d => d.id === r.resource_id);
            return {
              id: r.resource_id,
              type: r.type || defaultResource?.type || 'prompts',
              platform: r.platform || defaultResource?.platform || 'Website',
              title: r.title || defaultResource?.title,
              description: r.description || defaultResource?.description,
              icon: defaultResource?.icon || FileText,
              color: r.color || defaultResource?.color || '#8B5CF6',
              author: r.author || defaultResource?.author || '@techbaenae',
              path: r.path || defaultResource?.path || '/',
              tags: r.tags || defaultResource?.tags || [],
              tools: r.tools || defaultResource?.tools || [],
              isLab: defaultResource?.isLab || false,
              requiresAuth: defaultResource?.requiresAuth || false,
              labType: defaultResource?.labType,
              image_url: r.image_url,
              status: r.is_published ? undefined : 'draft',
            };
          };
          
          // Merge backend published with localStorage published IDs
          const allPublishedIds = new Set([
            ...backendPublished.map(r => r.resource_id),
            ...localPublishedIds
          ]);
          
          // Get published resources
          const published = [
            ...backendPublished.map(mapResource),
            ...defaultPublishedResources.filter(d => !backendResourceIds.has(d.id)),
            ...defaultDraftResources.filter(d => allPublishedIds.has(d.id) && !backendResourceIds.has(d.id))
          ];
          
          // Get draft resources: defaults not published
          const publishedIds = new Set(published.map(p => p.id));
          const drafts = [
            ...backendDrafts.map(mapResource),
            ...defaultDraftResources.filter(d => !publishedIds.has(d.id))
          ];
          
          setPublishedResources(published);
          setDraftResources(drafts);
          return;
        }
      } catch (error) {
        console.log('Backend unavailable, using localStorage fallback');
      }
      
      // Fallback: Use localStorage only
      if (localPublishedIds.length > 0) {
        const published = [
          ...defaultPublishedResources,
          ...defaultDraftResources.filter(d => localPublishedIds.includes(d.id)).map(d => {
            const { status, ...rest } = d;
            return rest;
          })
        ];
        const publishedIds = new Set(published.map(p => p.id));
        const drafts = defaultDraftResources.filter(d => !publishedIds.has(d.id));
        
        setPublishedResources(published);
        setDraftResources(drafts);
      }
    };
    
    loadResources();
  }, []);

  const handleResourceClick = (resource) => {
    if (resource.isLab && resource.requiresAuth) {
      // Check approval status specific to this lab
      const labKey = resource.labType || resource.id;
      const approved = localStorage.getItem(`${labKey}_approved`);
      if (approved === 'true') {
        navigate(resource.path);
      } else {
        setSelectedLab(resource);
        setShowLabAccessModal(true);
      }
      return;
    }
    navigate(resource.path);
  };

  const handlePublishResource = async (resource) => {
    // Move from drafts to published locally first for instant UI feedback
    const publishedResource = { ...resource };
    delete publishedResource.status;
    
    setDraftResources(prev => prev.filter(r => r.id !== resource.id));
    setPublishedResources(prev => [...prev, publishedResource]);
    
    // Always save to localStorage as fallback (works even when CORS blocks backend)
    const localPublishedIds = JSON.parse(localStorage.getItem('published_resource_ids') || '[]');
    if (!localPublishedIds.includes(resource.id)) {
      localPublishedIds.push(resource.id);
      localStorage.setItem('published_resource_ids', JSON.stringify(localPublishedIds));
    }
    
    // Try to save to backend (may fail due to CORS on production)
    try {
      // First ensure the resource exists in backend
      await fetch(`${API_URL}/api/admin/resources`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          resource_id: resource.id,
          type: resource.type || 'labs',
          title: resource.title,
          description: resource.description,
          color: resource.color || '#8B5CF6',
          author: resource.author || '',
          image_url: resource.image_url || '',
          path: resource.path || '/',
          tags: resource.tags || [],
          tools: resource.tools || [],
          is_published: true,
        }),
      });
      
      // Then explicitly publish it
      await fetch(`${API_URL}/api/admin/resources/${resource.id}/publish`, {
        method: 'POST',
      });
      
      console.log('Resource published to backend successfully');
    } catch (error) {
      console.log('Backend save failed (CORS), but localStorage fallback is active');
    }
  };

  const handleSaveResource = async (updatedResource) => {
    // Update in published or draft list locally
    if (updatedResource.status === 'draft') {
      setDraftResources(prev => 
        prev.map(r => r.id === updatedResource.id ? { ...r, ...updatedResource } : r)
      );
    } else {
      setPublishedResources(prev => 
        prev.map(r => r.id === updatedResource.id ? { ...r, ...updatedResource } : r)
      );
    }
    
    // Save to backend with all required fields
    try {
      const response = await fetch(`${API_URL}/api/admin/resources`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          resource_id: updatedResource.id,
          type: updatedResource.type || 'labs',
          title: updatedResource.title,
          description: updatedResource.description,
          color: updatedResource.color || '#8B5CF6',
          author: updatedResource.author || '',
          image_url: updatedResource.image_url || '',
          path: updatedResource.path || '/',
          tags: updatedResource.tags || [],
          tools: updatedResource.tools || [],
          is_published: updatedResource.status !== 'draft',
        }),
      });
      
      if (response.ok) {
        console.log('Resource saved successfully');
      } else {
        const error = await response.json();
        console.error('Failed to save resource:', error);
      }
    } catch (error) {
      console.error('Error saving resource:', error);
    }
  };

  const handleRemoveResource = async (resource) => {
    if (!window.confirm(`Are you sure you want to remove "${resource.title}"?`)) {
      return;
    }
    
    // Remove from published list and add back to drafts (unpublish)
    setPublishedResources(prev => prev.filter(r => r.id !== resource.id));
    
    // Add back to drafts with draft status
    const draftResource = { ...resource, status: 'draft' };
    setDraftResources(prev => [...prev, draftResource]);
    
    // Update localStorage
    const localPublishedIds = JSON.parse(localStorage.getItem('published_resource_ids') || '[]');
    const updatedIds = localPublishedIds.filter(id => id !== resource.id);
    localStorage.setItem('published_resource_ids', JSON.stringify(updatedIds));
    
    // Try to update backend
    try {
      await fetch(`${API_URL}/api/admin/resources/${resource.id}/unpublish`, {
        method: 'POST',
      });
      console.log('Resource unpublished successfully');
    } catch (error) {
      console.log('Backend unpublish failed, localStorage fallback active');
    }
  };

  return (
    <section 
      id="resources" 
      className="py-16 md:py-24 px-4 md:px-6 relative"
      aria-labelledby="resources-heading"
    >
      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header */}
        <motion.div 
          className="text-center space-y-3 md:space-y-4 mb-8 md:mb-12 relative"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div 
            className="absolute inset-0 blur-3xl opacity-50"
            style={{
              background: theme === 'sunrise' 
                ? 'radial-gradient(ellipse at center, rgba(251,191,36,0.4) 0%, transparent 70%)'
                : 'radial-gradient(ellipse at center, rgba(74,222,128,0.4) 0%, transparent 70%)',
            }}
          />
          <h2 
            id="resources-heading"
            className="text-2xl sm:text-3xl md:text-4xl font-bold relative z-10 text-white drop-shadow-lg"
          >
            brain food
          </h2>
          <p className="text-sm md:text-base text-white/70 relative z-10 max-w-xl mx-auto">
            Curated resources to help you build and grow
          </p>
        </motion.div>

        {/* Published Resources - Single Lab Card */}
        <div className="max-w-md mx-auto">
          {publishedResources
            .filter(resource => isAdmin || !resource.private) // Hide private resources from public
            .map((resource) => (
            <ResourceCard
              key={resource.id}
              resource={resource}
              onClick={() => handleResourceClick(resource)}
              isAdmin={isAdmin}
              onEdit={setEditingResource}
              onPublish={null}
              onRemove={handleRemoveResource}
            />
          ))}
        </div>

        {/* Coming Soon Note */}
        <motion.p
          className="text-center text-white/40 text-sm mt-8"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          More resources coming soon
        </motion.p>

        {/* Admin Drafts Section */}
        {isAdmin && (
          <motion.div
            className="mt-16 pt-8 border-t border-white/10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <button
              onClick={() => setShowDrafts(!showDrafts)}
              className="w-full flex items-center justify-between p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors mb-4"
              data-testid="admin-drafts-toggle"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <EyeOff className="w-5 h-5 text-purple-400" />
                </div>
                <div className="text-left">
                  <h3 className="text-white font-semibold">Admin Drafts</h3>
                  <p className="text-white/50 text-sm">{draftResources.length} unpublished resources</p>
                </div>
              </div>
              {showDrafts ? (
                <ChevronUp className="w-5 h-5 text-white/50" />
              ) : (
                <ChevronDown className="w-5 h-5 text-white/50" />
              )}
            </button>

            <AnimatePresence>
              {showDrafts && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {draftResources.map((resource) => (
                      <ResourceCard
                        key={resource.id}
                        resource={resource}
                        onClick={() => handleResourceClick(resource)}
                        isAdmin={isAdmin}
                        onEdit={setEditingResource}
                        onPublish={handlePublishResource}
                      />
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </div>

      {/* Lab Access Modal */}
      <AnimatePresence>
        {showLabAccessModal && (
          <LabAccessModal
            isOpen={showLabAccessModal}
            onClose={() => { setShowLabAccessModal(false); setSelectedLab(null); }}
            onAccessGranted={() => { setShowLabAccessModal(false); setSelectedLab(null); }}
            lab={selectedLab}
          />
        )}
      </AnimatePresence>

      {/* Admin Edit Modal */}
      <AnimatePresence>
        {editingResource && (
          <AdminEditModal
            isOpen={!!editingResource}
            onClose={() => setEditingResource(null)}
            resource={editingResource}
            onSave={handleSaveResource}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

// Export draft resources for admin page
export const getDraftResources = () => defaultDraftResources;

export default ResourcesSection;
